import { useState, useEffect } from 'react';
import { Server, Plus, Trash2, Code } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import CollapsibleSection from './CollapsibleSection';
import SaveToRepoButton from './SaveToRepoButton';
import { useClaudeConfig } from './useClaudeConfig';

interface McpServer {
  type: string;
  url?: string;
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  headers?: Record<string, string>;
}

type McpConfig = { mcpServers: Record<string, McpServer> };

export default function McpServersSection() {
  const config = useClaudeConfig('.mcp.json');
  const [servers, setServers] = useState<Record<string, McpServer>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [rawMode, setRawMode] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (config.content) {
      try {
        const parsed: McpConfig = JSON.parse(config.content);
        setServers(parsed.mcpServers || {});
      } catch {
        setServers({});
      }
    }
  }, [config.content]);

  function persist(updated: Record<string, McpServer>) {
    setServers(updated);
    config.setContent(JSON.stringify({ mcpServers: updated }, null, 2));
  }

  function updateServer(name: string, patch: Partial<McpServer>) {
    const server = { ...servers[name], ...patch };
    persist({ ...servers, [name]: server });
  }

  function addServer() {
    if (!newName.trim()) return;
    const safeName = newName.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    persist({ ...servers, [safeName]: { type: 'stdio', command: '', args: [] } });
    setSelected(safeName);
    setAdding(false);
    setNewName('');
  }

  function removeServer(name: string) {
    const next = { ...servers };
    delete next[name];
    persist(next);
    if (selected === name) setSelected(null);
  }

  function updateEnv(serverName: string, key: string, value: string) {
    const server = servers[serverName];
    updateServer(serverName, { env: { ...server.env, [key]: value } });
  }

  function removeEnv(serverName: string, key: string) {
    const server = servers[serverName];
    const env = { ...server.env };
    delete env[key];
    updateServer(serverName, { env });
  }

  const serverNames = Object.keys(servers);
  const current = selected ? servers[selected] : null;

  return (
    <CollapsibleSection
      title="MCP Servers"
      description="Model Context Protocol server connections"
      filePath=".mcp.json"
      icon={Server}
      exists={serverNames.length > 0}
      loading={config.loading}
    >
      {config.loading ? (
        <LoadingState size="md" />
      ) : (
        <div className="space-y-4">
          {/* Toggle raw JSON */}
          <div className="flex justify-end">
            <button
              onClick={() => setRawMode(!rawMode)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <Code className="w-3.5 h-3.5" />
              {rawMode ? 'Form view' : 'Raw JSON'}
            </button>
          </div>

          {rawMode ? (
            <textarea
              value={config.content || '{\n  "mcpServers": {}\n}'}
              onChange={(e) => config.setContent(e.target.value)}
              className="w-full h-64 p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100 resize-y"
            />
          ) : (
            <div className="flex gap-4">
              {/* Server list */}
              <div className="w-48 shrink-0 space-y-1">
                {serverNames.map((name) => (
                  <button
                    key={name}
                    onClick={() => setSelected(name)}
                    className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                      selected === name
                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Server className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{name}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{servers[name].type}</span>
                  </button>
                ))}

                {adding ? (
                  <div className="flex gap-1 mt-2">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addServer()}
                      placeholder="server-name"
                      autoFocus
                      className="flex-1 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100"
                    />
                    <button onClick={addServer} className="px-2 py-1 rounded bg-purple-600 text-white text-xs">
                      OK
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAdding(true)}
                    className="w-full flex items-center gap-1 px-3 py-1.5 rounded text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add server
                  </button>
                )}
              </div>

              {/* Server editor */}
              {current && selected ? (
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Type</label>
                      <select
                        value={current.type}
                        onChange={(e) => updateServer(selected, { type: e.target.value })}
                        className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                      >
                        <option value="stdio">stdio</option>
                        <option value="http">http</option>
                        <option value="sse">sse</option>
                      </select>
                    </div>
                    {current.type === 'stdio' ? (
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Command</label>
                        <input
                          type="text"
                          value={current.command || ''}
                          onChange={(e) => updateServer(selected, { command: e.target.value })}
                          placeholder="npx"
                          className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono text-slate-900 dark:text-slate-100"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">URL</label>
                        <input
                          type="text"
                          value={current.url || ''}
                          onChange={(e) => updateServer(selected, { url: e.target.value })}
                          placeholder="https://..."
                          className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono text-slate-900 dark:text-slate-100"
                        />
                      </div>
                    )}
                  </div>

                  {current.type === 'stdio' && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Args <span className="text-slate-400">(comma-separated)</span>
                      </label>
                      <input
                        type="text"
                        value={(current.args || []).join(', ')}
                        onChange={(e) =>
                          updateServer(selected, {
                            args: e.target.value
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                        placeholder="-y, @playwright/mcp@latest"
                        className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono text-slate-900 dark:text-slate-100"
                      />
                    </div>
                  )}

                  {/* Environment variables */}
                  <fieldset className="space-y-2">
                    <legend className="text-xs text-slate-500">Environment variables</legend>
                    {Object.entries(current.env || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 text-xs font-mono">
                        <span className="text-slate-600 dark:text-slate-400">{key}</span>
                        <span className="text-slate-400">=</span>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateEnv(selected, key, e.target.value)}
                          className="flex-1 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100"
                        />
                        <button onClick={() => removeEnv(selected, key)} className="text-red-400 hover:text-red-600 text-xs">
                          remove
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="KEY"
                        className="w-28 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                            updateEnv(selected, e.currentTarget.value.trim(), '');
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <span className="text-xs text-slate-400">press Enter to add</span>
                    </div>
                  </fieldset>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => removeServer(selected)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-3 h-3" /> Remove server
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-slate-400 py-8">
                  {serverNames.length > 0 ? 'Select a server to configure' : 'Add your first MCP server'}
                </div>
              )}
            </div>
          )}

          <SaveToRepoButton saving={config.saving} saved={config.saved} error={config.error} onSave={() => config.save()} />
        </div>
      )}
    </CollapsibleSection>
  );
}
